import React, { useContext, useState } from "react";
import { Button, message, Modal, Space, Typography, Spin, theme } from "antd";
import { ExternalLink, AlertCircle } from "lucide-react";
import { env } from "process";
import { IntegrationOperation } from "../../../../../serviceoperation/IntegrationOperation";
import { CompanyContext } from "../../../../../contexts/company-context";
import { set } from "husky";

const { Text, Paragraph } = Typography;

const QuickBooksIntegration: React.FC = () => {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { selectedCompany } = useContext(CompanyContext);

  const startOAuthFlow = async () => {
    try {
      setLoading(true);

      const intOps = new IntegrationOperation();
      const response = await intOps.getCorpIntegrationsAuthURLAync(
        selectedCompany?.corporationId as number,
        "QuickBooksEmployeeProvider"
      );

      if (response?.statusCode === 200 && response?.data) {
        // Redirect = app teardown, no need to unset loading
        window.location.href = response.data;
        return;
      }

      message.error("Failed to get QuickBooks authorization URL.");
    } catch (error) {
      message.error(
        "An error occurred while getting QuickBooks authorization URL."
      );
      setLoading(false); // only reached if redirect didn't happen
    }
  };

  return (
    <>
      <Button
        type="primary"
        icon={<ExternalLink size={16} />}
        onClick={() => setShowConfirm(true)}
        loading={loading}
        style={{ borderRadius: 6 }}
      >
        Connect QuickBooks
      </Button>

      <Modal
        title="Connect to QuickBooks Online"
        open={showConfirm}
        onOk={startOAuthFlow}
        onCancel={() => setShowConfirm(false)}
        okText="Continue to QuickBooks"
        cancelText="Cancel"
        confirmLoading={loading}
        width={500}
      >
        <Spin spinning={loading}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Paragraph>
              You'll be redirected to QuickBooks to authorize access. Make sure
              you're signed into the correct QuickBooks account.
            </Paragraph>

            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorInfoBg,
                borderColor: token.colorInfoBorder,
                border: `1px solid ${token.colorInfoBorder}`,
                borderRadius: 6,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <AlertCircle
                size={18}
                style={{ color: token.colorInfo, flexShrink: 0 }}
              />
              <div>
                <Text
                  strong
                  style={{
                    color: token.colorInfo,
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  What we'll access
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    display: "block",
                  }}
                >
                  • Employee and payroll data
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    display: "block",
                  }}
                >
                  • Company information
                </Text>
              </div>
            </div>

            <Paragraph style={{ fontSize: 12, margin: 0 }}>
              By connecting, you agree to sync selected data with your
              QuickBooks account. You can disconnect anytime from settings.
            </Paragraph>
          </Space>
        </Spin>
      </Modal>
    </>
  );
};

export default QuickBooksIntegration;
